/**
 * The infrastructure map (2026-09-02, DECISIONS #86; re-cut 2026-09-03,
 * DECISIONS #87). The product's runtime picture per environment, drawn from
 * the deploy manifests with the live sample folded in when a source is
 * connected, and never by a model: it is always there and always true. It
 * is NOT a picture of the conversation (owner: "is this runtime of the app
 * or my question?"), and the Infrastructure tab's tooltip says so.
 *
 * Every drawn node is clickable (owner: "nothing is clickable here unlike
 * the product map"): the server names each one as a part keyed by its
 * mermaid id, so the client finds the shape by id rather than by matching
 * text, and a click docks that part's facts along the bottom, the way the
 * answer diagrams do. The environment switcher on the pane's strip is the
 * same store the part panel's live numbers read, so one choice moves both.
 * The view draws no header of its own (2026-09-05; owner: "this can be
 * designed better"): its status line rides the summary bar under the
 * drawing.
 *
 * Under the drawing, what the analysis found, with the three words explained
 * (owner: "what is the bottom section with drifts and all that?"): a Risk
 * can lose work or fail, a Drift is what runs differing from what the code
 * declares, a Note is worth knowing. A finding is a button that stages a
 * question.
 *
 * In the pane the picture is THIS QUESTION's slice of the infrastructure
 * (DECISIONS #88; 2026-09-05, owner: "I don't need whole product view.
 * Only this question"), at the altitude the product diagram uses: names
 * on the boxes, everything else on the part a click opens, and a group too
 * big to read drawn as one box. There is no whole-product toggle here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { RuntimeFinding, RuntimePart, Ulid } from '../../api/types';
import { formatRelativeTime } from '../../lib/format';
import { ErrorNote, SkeletonLine } from '../../lib/ui';
import { useChatStore } from '../../stores/chatStore';
import { MermaidDiagram } from '../chat/MermaidDiagram';
import { newestAnswer, type SessionDiagram } from '../chat/transcript';
import { answerQuotes } from './answerQuotes';
import { useRuntimeMap, type RuntimeMapScope } from '../liveinfra/api';
import { useViewEnv } from '../liveinfra/envStore';
import { clampViewEnv } from '../liveinfra/types';
import { DiagramView } from './DiagramView';
import { FindingButton, findingQuestion } from './FindingButton';
import { decorateParts, markSelectedPart } from './partHotspots';
import { RuntimePartPanel } from './RuntimePartPanel';

/** The three words, explained once, where they are used. */
export const FINDINGS_LEGEND =
  'Risk: can lose work or fail. Drift: what runs differs from what the code declares. Note: worth knowing.';

const SEVERITY_GLOSS: Record<string, string> = {
  risk: 'Risk: can lose work or fail.',
  drift: 'Drift: what runs differs from what the code declares.',
  note: 'Note: worth knowing.',
};

/**
 * Only the words the findings on screen actually use: one Note does not
 * need Risk and Drift explained beside it (audit 2026-09-07).
 */
export function findingsLegendFor(findings: readonly { severity: string }[]): string {
  const present = ['risk', 'drift', 'note'].filter((s) => findings.some((f) => f.severity === s));
  return present.map((s) => SEVERITY_GLOSS[s]).join(' ');
}

/** How many findings show before "Show all". */
const FINDINGS_FOLD = 6;

export { findingQuestion };

/**
 * Make the drawn nodes buttons. Mermaid renders each node as
 * `g.node#flowchart-<id>-<n>`; the server's part carries `<id>`, so the join
 * is by id, not by text. Returns the undo.
 */
function escapeRe(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function decorateRuntimeNodes(
  root: HTMLElement,
  parts: readonly RuntimePart[],
  onSelect: (id: string) => void,
): () => void {
  // Mermaid names a node's group after its id, with a render-run suffix; an
  // edge's label group carries `L_<from>_<to>_<n>` on its inner label
  // (2026-09-06: every drawn line is a part too), and the part names the two
  // ends as `edge:<from>:<to>`.
  const nodes = Array.from(root.querySelectorAll('g.node'));
  const edgeLabels = Array.from(root.querySelectorAll('g.edgeLabel'));
  const findEdge = (node: string): Element | null => {
    const [, from, to] = node.split(':');
    if (from === undefined || to === undefined) return null;
    const re = new RegExp(`^L[-_]${escapeRe(from)}[-_]${escapeRe(to)}[-_]\\d+$`);
    return (
      edgeLabels.find((g) => {
        const id = g.querySelector('[data-id]')?.getAttribute('data-id') ?? g.id;
        return re.test(id);
      }) ?? null
    );
  };
  return decorateParts(parts, {
    find: (part) =>
      part.node.startsWith('edge:')
        ? findEdge(part.node)
        : (nodes.find((n) => n.id.includes(`${part.node}-`) || n.id.endsWith(part.node)) ?? null),
    key: (part) => part.id,
    label: (part) => part.label,
    ariaLabel: (part) => `About ${part.label}`,
    extraAttrs: (part) => ({ 'data-runtime-part': part.id }),
    onSelect,
  }).cleanup;
}


export function RuntimeMapView({
  repoId,
  scope,
  title = null,
  board = null,
  sessionId = null,
}: {
  repoId: Ulid;
  /** the board's title, shown as is: one title for both views (2026-09-06) */
  title?: string | null;
  /** the board itself: its parts say what of the answer runs where */
  board?: SessionDiagram | null;
  /** the conversation, for what the answer said about a part */
  sessionId?: Ulid | null;
  /**
   * The slice a question touched (DECISIONS #88): the server cuts the map to
   * these dirs and services plus one hop. Absent = the whole product.
   */
  scope?: (RuntimeMapScope & { question: string }) | undefined;
}) {
  const scoped = scope !== undefined;
  const map = useRuntimeMap(repoId, scoped ? scope : undefined);
  const wantedEnv = useViewEnv(repoId);
  // held to the environments this map has (review 2026-09-07)
  const env = clampViewEnv(wantedEnv, Object.keys(map.data?.environments ?? {}));
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [rendered, setRendered] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const envRows = map.data?.environments[env] ?? map.data?.environments[map.data?.default_environment ?? ''];
  const parts = useMemo(() => envRows?.parts ?? [], [envRows]);
  const answerText = useChatStore((st) =>
    sessionId !== null ? (newestAnswer(st.diagramSource[sessionId]?.messages ?? [])?.text ?? null) : null,
  );

  const onState = useCallback((state: 'rendered' | 'failed') => {
    if (state === 'rendered') setRendered((n) => n + 1);
  }, []);

  // The hotspots, rebuilt whenever the drawing is (a render, an environment).
  useEffect(() => {
    const root = hostRef.current;
    if (root === null || parts.length === 0) return;
    return decorateRuntimeNodes(root, parts, setOpenId);
  }, [rendered, parts]);

  const openPart = parts.find((p) => p.id === openId) ?? null;
  // What of the answer lives here: the board parts whose "runs on" phrase
  // named this part (the server kept the phrases), and the answer's sentences.
  const boardParts = useMemo(
    () =>
      openPart === null || board === null
        ? []
        : board.parts
            .filter((bp) => bp.runs_on !== null && (openPart.question ?? []).includes(bp.runs_on))
            .map((bp) => ({ label: bp.label, what: bp.what })),
    [openPart, board],
  );
  const quotes = useMemo(
    () => (openPart === null ? [] : answerQuotes(answerText, openPart.label)),
    [openPart, answerText],
  );
  useEffect(() => {
    markSelectedPart(hostRef.current, openPart?.label ?? null);
  }, [openPart, rendered]);

  // A new environment is a new picture: nothing stays open across it. The
  // environment the open part belongs to is remembered so the reset happens
  // in render, before the new picture is ever shown with the old part open.
  const [openEnv, setOpenEnv] = useState(env);
  if (openEnv !== env) {
    setOpenEnv(env);
    setOpenId(null);
  }

  useEffect(() => {
    if (openId === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target !== null && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.key === 'Escape') setOpenId(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openId]);

  if (map.isPending) {
    return (
      <div className="p-4" data-testid="runtime-map-pending">
        <SkeletonLine height={160} />
      </div>
    );
  }
  if (map.isError) {
    return (
      <div className="p-4">
        <ErrorNote>{map.error.message}</ErrorNote>
      </div>
    );
  }
  const doc = map.data;
  const envDoc = doc.environments[env] ?? doc.environments[doc.default_environment];
  if (envDoc === undefined) return null;
  const live = doc.live;
  // Which source is live decides the words: Render lists the services;
  // a connection string alone reads queue depths and nothing about them.
  const renderLive = live.sources['render'] !== undefined && live.sources['render'].status !== 'off';
  const sampled = `sampled ${live.sampled_at !== null ? formatRelativeTime(live.sampled_at) : 'not yet'}`;
  const status = renderLive
    ? `live from Render · ${sampled}`
    : live.connected
      ? `queue depths live from Redis · services from the manifests · ${sampled}`
      : 'from the deploy manifests only · connect a live source for what is configured';
  const changed = doc.history[0]?.diff;
  const matched = doc.scope?.matched ?? [];
  const sliceNote = scoped
    ? matched.length > 0
      ? `this question touches ${matched.length} service${matched.length === 1 ? '' : 's'}`
      : 'nothing here maps to this question, so this is the whole product'
    : null;
  const findings: readonly RuntimeFinding[] = envDoc.findings;
  const shown = showAll ? findings : findings.slice(0, FINDINGS_FOLD);
  const ask = (question: string) => setComposerDraft(question);

  return (
    <div
      data-testid="runtime-map"
      data-environment={env}
      className="flex min-h-0 flex-1 flex-col"
    >
      {/* The title row, the same as the board's (2026-09-06; owner: "How come
          the title only appears in product view and not in infra view?"). */}
      <div className="flex shrink-0 items-center gap-2 px-4 pt-3">
        <p
          data-testid="runtime-map-title"
          className="mt-0 mb-0 min-w-0 flex-1 truncate text-[length:var(--text-ui-size)] leading-[var(--text-ui-line)] font-medium text-primary"
        >
          {title ?? ''}
        </p>
      </div>
      <div className="relative min-h-0 flex-1">
        <DiagramView
          resetKey={`${repoId}:${env}:${doc.content_hash}`}
          onBackgroundClick={() => setOpenId(null)}
        >
          <div ref={hostRef} className="relative w-max">
            <MermaidDiagram bare code={envDoc.mermaid} onState={onState} />
          </div>
        </DiagramView>
      </div>

      {openPart !== null ? (
        <RuntimePartPanel
          part={openPart}
          env={env}
          findings={findings}
          sampledAt={live.sampled_at}
          boardParts={boardParts}
          quotes={quotes}
          onAsk={ask}
          onClose={() => setOpenId(null)}
        />
      ) : null}

      <div className="shrink-0 border-t border-default px-4 py-2">
        <p
          data-testid="runtime-map-status"
          className="mt-0 mb-0.5 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
        >
          {status}
          {sliceNote !== null ? ` · ${sliceNote}` : ''}
        </p>
        <p
          data-testid="runtime-map-summary"
          className="mt-0 mb-1 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
        >
          {env}: {envDoc.summary}
          {changed !== undefined && changed.total > 0
            ? ` · since the last look: ${describeDiff(changed)}`
            : ''}
        </p>
        {findings.length > 0 ? (
          <>
            <div className="mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className="text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary">
                What the analysis found · {findings.length}
              </span>
              <span
                data-testid="runtime-findings-legend"
                className="text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
              >
                {findingsLegendFor(findings)}
              </span>
            </div>
            <ul data-testid="runtime-findings" className="m-0 max-h-44 list-none space-y-1 overflow-y-auto p-0">
              {shown.map((f) => (
                <li key={f.id}>
                  <FindingButton finding={f} onAsk={ask} />
                </li>
              ))}
            </ul>
            {findings.length > FINDINGS_FOLD ? (
              <button
                type="button"
                data-testid="runtime-findings-toggle"
                onClick={() => setShowAll((was) => !was)}
                className="mt-1 cursor-pointer rounded-chip border-none bg-transparent px-1 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary hover:text-primary"
              >
                {showAll ? 'Show fewer' : `Show all ${findings.length}`}
              </button>
            ) : null}
          </>
        ) : (
          <p className="mt-0 mb-0 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary">
            Nothing to flag in {env}.
          </p>
        )}
      </div>
    </div>
  );
}

/** How many clauses the summary line carries before "and N more". */
const DIFF_CLAUSES = 4;

/** "1 service added, worker-prod 1 → 3 instances, 2 findings resolved". */
export function describeDiff(diff: {
  services_added: string[];
  services_removed: string[];
  live_appeared?: string[];
  live_lost?: string[];
  instances_changed: Array<{ id: string; from: number | null; to: number | null }>;
  deploys_changed: Array<{ id: string; from: string | null; to: string | null }>;
  statuses_changed: Array<{ id: string; from: string | null; to: string | null }>;
  findings_appeared: string[];
  findings_resolved: string[];
}): string {
  const parts: string[] = [];
  const count = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  if (diff.services_added.length > 0) parts.push(`${count(diff.services_added.length, 'service')} added`);
  if (diff.services_removed.length > 0) parts.push(`${count(diff.services_removed.length, 'service')} gone`);
  const appeared = diff.live_appeared ?? [];
  const lost = diff.live_lost ?? [];
  if (appeared.length > 0) {
    parts.push(
      appeared.length <= 2
        ? `${appeared.map((id) => id.replace(/^svc:/, '')).join(' and ')} now observed on Render`
        : `${count(appeared.length, 'service')} now observed on Render`,
    );
  }
  if (lost.length > 0) parts.push(`${count(lost.length, 'service')} no longer observed`);
  for (const c of diff.instances_changed) {
    parts.push(`${c.id.replace(/^svc:/, '')} ${c.from ?? '?'} → ${c.to ?? '?'} instances`);
  }
  for (const c of diff.deploys_changed) {
    parts.push(`${c.id.replace(/^svc:/, '')} deployed ${c.to ?? '?'}`);
  }
  for (const c of diff.statuses_changed) {
    parts.push(`${c.id.replace(/^svc:/, '')} now ${c.to ?? 'unknown'}`);
  }
  if (diff.findings_appeared.length > 0) parts.push(`${count(diff.findings_appeared.length, 'new finding')}`);
  if (diff.findings_resolved.length > 0) parts.push(`${count(diff.findings_resolved.length, 'finding')} resolved`);
  if (parts.length > DIFF_CLAUSES) {
    const rest = parts.length - DIFF_CLAUSES;
    return `${parts.slice(0, DIFF_CLAUSES).join(', ')}, and ${rest} more`;
  }
  return parts.join(', ');
}
